import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import AnalogClock from './AnalogClock';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { normalizeText, wordSetOf, isSubsetOf, setsEqual } from '../../lib/speechAnswerEvaluator';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './Clocks.css';

// Уровень 1, 2 и Хитрые задания. validAnswers перечисляет все допустимые
// формулировки правильного времени (в т.ч. разговорные — "без четверти",
// "пол седьмого") — они передаются распознавателю речи как словарь для
// текущего вопроса, поэтому распознаются наравне с числительными.
const EXERCISE_DATA = [
  // Базовые
  { id: 1, hours: 3, minutes: 0, validAnswers: ['три часа', 'три', 'три ноль ноль', 'пятнадцать часов'] },
  { id: 2, hours: 4, minutes: 30, validAnswers: ['четыре тридцать', 'половина пятого', 'четыре часа тридцать минут', 'шестнадцать тридцать'] },
  { id: 3, hours: 5, minutes: 15, validAnswers: ['пять пятнадцать', 'четверть шестого', 'пять часов пятнадцать минут', 'семнадцать пятнадцать'] },
  { id: 4, hours: 6, minutes: 45, validAnswers: ['шесть сорок пять', 'без пятнадцати семь', 'без четверти семь', 'восемнадцать сорок пять'] },
  { id: 5, hours: 7, minutes: 10, validAnswers: ['семь десять', 'десять минут восьмого', 'семь часов десять минут', 'девятнадцать десять'] },
  { id: 6, hours: 8, minutes: 20, validAnswers: ['восемь двадцать', 'двадцать минут девятого', 'восемь часов двадцать минут', 'двадцать двадцать'] },
  { id: 7, hours: 9, minutes: 0, validAnswers: ['девять часов', 'девять', 'девять ноль ноль', 'двадцать один час'] },
  { id: 8, hours: 10, minutes: 40, validAnswers: ['десять сорок', 'без двадцати одиннадцать', 'десять часов сорок минут', 'двадцать два сорок'] },

  // ХИТРЫЕ ЗАДАНИЯ (Разговорные выражения)
  { id: 9, hours: 6, minutes: 30, validAnswers: ['шесть тридцать', 'пол седьмого', 'половина седьмого', 'шесть часов тридцать минут'] },
  { id: 10, hours: 2, minutes: 45, validAnswers: ['два сорок пять', 'без четверти три', 'без пятнадцати три', 'два часа сорок пять минут'] },
  { id: 11, hours: 4, minutes: 15, validAnswers: ['четыре пятнадцать', 'четверть пятого', 'четыре часа пятнадцать минут', 'шестнадцать пятнадцать'] },
];

// Словарь для «канонической» озвучки часов и минут (кратны 5) — используется
// только для вариантов-дистракторов, у которых нет собственного набора
// разговорных формулировок.
const HOUR_WORDS = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять', 'десять', 'одиннадцать', 'двенадцать'];

const MINUTE_WORDS = {
  0: '', 5: 'пять', 10: 'десять', 15: 'пятнадцать', 20: 'двадцать', 25: 'двадцать пять',
  30: 'тридцать', 35: 'тридцать пять', 40: 'сорок', 45: 'сорок пять', 50: 'пятьдесят', 55: 'пятьдесят пять',
};

const hourSuffix = (hours) => {
  if (hours === 1) return 'час';
  if (hours >= 2 && hours <= 4) return 'часа';
  return 'часов';
};

const timeToWords = (hours, minutes) => {
  const hourWord = HOUR_WORDS[hours] || '';
  if (minutes === 0) return `${hourWord} ${hourSuffix(hours)}`.trim();
  return `${hourWord} ${MINUTE_WORDS[minutes] || ''}`.trim();
};

const formatTime = (hours, minutes) => `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

// Если время дистрактора совпадает с одним из заданий в EXERCISE_DATA, для
// него можно использовать тот же набор разговорных формулировок (например,
// дистрактор "3:00" получит и "три часа", и "три ноль ноль", а не только
// одну каноническую фразу) — иначе используем единственную каноническую
// формулировку, сгенерированную числительными.
const VALID_ANSWERS_BY_TIME = EXERCISE_DATA.reduce((acc, item) => {
  acc[`${item.hours}:${item.minutes}`] = item.validAnswers;
  return acc;
}, {});
const answersForTime = (hours, minutes) => VALID_ANSWERS_BY_TIME[`${hours}:${minutes}`] || [timeToWords(hours, minutes)];

const Clocks = ({ ageGroup, onBack, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  
  const [selectedMode, setSelectedMode] = useState(null);
  const [blinkTarget, setBlinkTarget] = useState('speak');
  
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeFeedback, setActiveFeedback] = useState(null);
  const [clickedOption, setClickedOption] = useState(null);
  const [recognizerReady, setRecognizerReady] = useState(false);

  const { start: startSpeakRecognition, stop: stopSpeakRecognition } = useSpeakAnswer('Clocks');
  const { play: playInstructionAudio, stop: stopInstructionAudio } = useInstructionAudio();

  useEffect(() => {
    const shuffled = [...EXERCISE_DATA].sort(() => Math.random() - 0.5);
    setQuestions(shuffled);
  }, []);

  useEffect(() => {
    if (selectedMode) return;
    const interval = setInterval(() => {
      setBlinkTarget(prev => (prev === 'speak' ? 'show' : 'speak'));
    }, 700);
    return () => clearInterval(interval);
  }, [selectedMode]);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  // Генерация вариантов ответа (без повторений). Мемоизируется по вопросу,
  // чтобы порядок карточек и словарь распознавания не пересчитывались (и не
  // тасовались заново) при каждом ре-рендере состояния.
  const options = useMemo(() => {
    if (!currentQuestion) return [];
    return [
      { hours: currentQuestion.hours, minutes: currentQuestion.minutes, correct: true },
      { hours: currentQuestion.hours, minutes: currentQuestion.minutes === 30 ? 15 : (currentQuestion.minutes === 0 ? 30 : 0), correct: false },
      { hours: currentQuestion.hours === 12 ? 1 : currentQuestion.hours + 1, minutes: currentQuestion.minutes, correct: false }
    ].sort(() => Math.random() - 0.5);
  }, [currentQuestion]);

  // Словарь для распознавателя речи, как в DaysOfWeek/Seasons: правильный
  // ответ даёт все свои разговорные формулировки (каждая — отдельный
  // кандидат с ключом 'correct'), а дистракторы — по одной канонической
  // формулировке с ключом 'wrong-<индекс варианта>', чтобы при распознавании
  // неверного времени можно было подсветить именно ту карточку.
  const timeCandidates = useMemo(() => {
    if (!currentQuestion) return [];
    const correctCandidates = currentQuestion.validAnswers.map((text) => ({ key: 'correct', text }));
    const wrongCandidates = options.flatMap((opt, idx) =>
      opt.correct ? [] : answersForTime(opt.hours, opt.minutes).map((text) => ({ key: `wrong-${idx}`, text }))
    );
    return [...correctCandidates, ...wrongCandidates];
  }, [currentQuestion, options]);

  // Слова каждой формулировки правильного/неправильного ответа —
  // используются, чтобы накапливать сказанное ребёнком по нескольким
  // репликам
  const correctWordSets = useMemo(
    () => (currentQuestion ? currentQuestion.validAnswers.map(wordSetOf) : []),
    [currentQuestion]
  );
  const wrongWordSets = useMemo(
    () =>
      options.flatMap((opt, idx) =>
        opt.correct ? [] : answersForTime(opt.hours, opt.minutes).map((text) => ({ idx, words: wordSetOf(text) }))
      ),
    [options]
  );

  const stopAll = useCallback(() => {
    stopSpeakRecognition();
    stopInstructionAudio();
  }, [stopSpeakRecognition, stopInstructionAudio]);

  const goToNextQuestion = () => {
    setCurrentIndex(prev => {
      if (prev < totalQuestions - 1) return prev + 1;
      setShowResult(true);
      return prev;
    });
  };

  const answeredRef = useRef(false);
  const heardWordsRef = useRef(new Set());

  const finishAnswer = (isCorrect, matchedOption = null) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    stopAll();

    setIsSpeaking(false);
    setIsListening(false);
    setActiveFeedback(isCorrect ? 'correct' : 'wrong');
    setClickedOption(matchedOption);
    setAnswers(prev => [...prev, isCorrect]);

    setTimeout(() => {
      setActiveFeedback(null);
      setClickedOption(null);
      setIsProcessing(false);
      goToNextQuestion();
    }, 1200);
  };

  const handleRawSpeechChunk = (text) => {
    if (answeredRef.current) return;
    normalizeText(text).split(' ').filter(Boolean).forEach((w) => heardWordsRef.current.add(w));
    const heard = heardWordsRef.current;
    if (heard.size === 0) return;

    if (correctWordSets.some((set) => setsEqual(set, heard))) {
      finishAnswer(true, options.find(opt => opt.correct) || null);
      return;
    }

    const stillPossible = correctWordSets.some((set) => isSubsetOf(heard, set));
    if (!stillPossible) {
      const matchedWrong = wrongWordSets.find((w) => setsEqual(w.words, heard));
      finishAnswer(false, matchedWrong ? options[matchedWrong.idx] : null);
    }
  };

  const processQuestion = useCallback(() => {
    if (!currentQuestion || showResult || !selectedMode) return;
    stopAll();

    setIsListening(true);
    setIsSpeaking(false);
    setIsProcessing(false);
    setActiveFeedback(null);
    setClickedOption(null);
    setRecognizerReady(false);
    answeredRef.current = false;
    heardWordsRef.current = new Set();

    const beginListeningOrInput = () => {
      setIsListening(false);
      setIsSpeaking(true);

      if (selectedMode === 'speak') {
        setIsProcessing(true);
        startSpeakRecognition(timeCandidates, {
          // Внутренний matchCandidate (per-chunk) отключён высоким порогом —
          // сопоставление многословных ответов, накопленных по нескольким
          // репликам, полностью берёт на себя handleRawSpeechChunk.
          threshold: 2,
          onListening: () => setRecognizerReady(true),
          onRawText: handleRawSpeechChunk,
          onResult: ({ matchedKey, timedOut }) => {
            if (!timedOut) return; // matchedKey never resolves internally (threshold: 2) — this is the timeout fallback
            finishAnswer(false, null);
          },
        });
      }
    };

    playInstructionAudio('Посмотри на часы. Который час?', beginListeningOrInput);
  }, [currentQuestion, showResult, selectedMode, playInstructionAudio, stopAll, startSpeakRecognition, timeCandidates, options, correctWordSets, wrongWordSets]);

  useEffect(() => {
    if (selectedMode) {
      processQuestion();
    }
  }, [currentIndex, selectedMode, processQuestion]);

  useEffect(() => {
    return () => stopAll();
  }, [stopAll]);

  useEffect(() => {
    if (!showResult) return;
    const timer = setTimeout(() => {
      const correctCount = answers.filter(a => a === true).length;
      const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
      if (onComplete) onComplete({ testId: 'Clocks', score });
      else onBack();
    }, 3000);
    return () => clearTimeout(timer);
  }, [showResult, answers, totalQuestions, onComplete, onBack]);

  const handleOptionClick = (option) => {
    if (selectedMode !== 'show' || isProcessing || activeFeedback) return;
    setIsProcessing(true);
    finishAnswer(option.correct, option);
  };

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate progress={answers} totalQuestions={totalQuestions} forceStop={true} onComplete={onComplete || onBack}>
        <div className="clocks-result-content">
          <h2 className="clocks-result-title">🎉 Упражнение завершено!</h2>
          <div className="clocks-result-score">
            <span className="clocks-score-number">{correctCount}</span>
            <span className="clocks-score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) {
    return (
      <ModuleTemplate progress={[]} totalQuestions={1} forceStop={true} onComplete={onComplete || onBack}>
        <div className="clocks-loading-text">Загрузка...</div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalQuestions} 
      forceStop={true} 
      onComplete={onComplete || onBack}
      disabled={!selectedMode || isProcessing}
      extraContent="Который час?"
    >
      <div className="clocks-game-area">
        
        {!selectedMode && (
          <div className="clocks-mode-overlay">
            <h2 className="clocks-mode-title">Выбери режим</h2>
            <div className="clocks-mode-buttons">
              <button 
                className="clocks-mode-btn" 
                onClick={() => setSelectedMode('speak')}
                style={{
                  borderColor: blinkTarget === 'speak' ? '#7B4EF8' : '#E5E0F5',
                  background: blinkTarget === 'speak' ? '#F0EDFF' : '#FFFFFF',
                  boxShadow: blinkTarget === 'speak' ? '0 4px 12px rgba(123, 78, 248, 0.15)' : 'none'
                }}
              >
                🎙️ Говорить
              </button>
              <button 
                className="clocks-mode-btn" 
                onClick={() => setSelectedMode('show')}
                style={{
                  borderColor: blinkTarget === 'show' ? '#7B4EF8' : '#E5E0F5',
                  background: blinkTarget === 'show' ? '#F0EDFF' : '#FFFFFF',
                  boxShadow: blinkTarget === 'show' ? '0 4px 12px rgba(123, 78, 248, 0.15)' : 'none'
                }}
              >
                👆 Показать
              </button>
            </div>
          </div>
        )}

        <div className="clocks-main-container">
          
          {selectedMode && (
            <div className="clocks-indicators">
              <div className={`clocks-indicator listen ${isListening ? 'active' : ''}`}>
                <span>🎧</span>
              </div>
              <div className={`clocks-indicator speak ${isSpeaking && (selectedMode !== 'speak' || recognizerReady) ? 'active' : ''}`}>
                <span>🗣️</span>
              </div>
            </div>
          )}

          <div className="clocks-analog-wrapper" style={{ opacity: !selectedMode ? 0.5 : 1, transition: 'opacity 0.3s' }}>
            <AnalogClock hours={currentQuestion.hours} minutes={currentQuestion.minutes} size={280} />
          </div>

          <div className="clocks-options-grid">
            {options.map((opt, idx) => {
              const isCorrectChoice = activeFeedback === 'correct' && opt.correct;
              const isWrongClick = activeFeedback === 'wrong' && opt === clickedOption;
              
              return (
                <div 
                  key={idx} 
                  className={`clocks-digital-card ${isCorrectChoice ? 'correct' : ''} ${isWrongClick ? 'wrong-click' : ''}`}
                  onClick={() => handleOptionClick(opt)}
                  style={{ 
                    pointerEvents: selectedMode === 'show' && !isProcessing ? 'auto' : 'none',
                    opacity: !selectedMode ? 0.5 : (selectedMode === 'show' ? 1 : 0.5)
                  }}
                >
                  {formatTime(opt.hours, opt.minutes)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </ModuleTemplate>
  );
};

export default Clocks;